import {
  Text,
  Stack,
  Button,
  VStack,
  Box,
  HStack,
  Image,
  Icon,
  Link,
  StackProps,
  useBreakpointValue,
  IconButton,
} from "@chakra-ui/react";
import { KYDWhitelabelEvent as IKYDEvent } from "@common/interfaces/KYDEvent";
import {
  FaChevronDown,
  FaChevronUp,
  FaInstagram,
  FaSpotify,
  FaTiktok,
} from "react-icons/fa";
import dayjs from "dayjs";

const platformToIcons = {
  instagram: FaInstagram,
  spotify: FaSpotify,
  tiktok: FaTiktok,
};

interface LineupProps {
  performers: IKYDEvent["performers"];
  onToggleSetTimes: () => void;
  isSetTimesExpanded: boolean;
  //display: StackProps["display"];
}

const formatDateToHours = (date: string) => {
  if (!date) return "";

  const dateObj = dayjs(date);
  return dateObj.format("h:mm A");
};

const PerformerRow = ({
  performer,
  isLast = false,
}: {
  performer: IKYDEvent["performers"][0];
  isLast?: boolean;
}) => {
  return (
    <HStack
      w="full"
      alignItems="center"
      justifyContent="center"
      borderBottom={isLast ? "none" : "1px solid"}
      borderColor={{ base: "#333333", md: "gray.200" }}
      py={2}
    >
      <Box
        rounded="full"
        alignItems="center"
        justifyContent="center"
        display="flex"
        w="64px"
        h="64px"
        overflow="hidden"
        flexShrink={0}
      >
        <Image
          src={performer.image_url}
          alt={performer.name}
          objectFit={"cover"}
        />
      </Box>
      <HStack w="full" flexGrow={1} gap={2} justifyContent="space-between">
        <Stack
          direction={{ base: "column", md: "row" }}
          alignItems="flex-start"
          justifyContent={{ base: "center", md: "space-between" }}
          flexGrow={1}
          gap={[1, 0]}
          spacing={[1, 0]}
        >
          <Text fontWeight="bold" color={{ base: "white", md: "black" }}>
            {performer.name}
          </Text>
          <HStack flexWrap="wrap">
            {[performer.instagram, performer.spotify, performer.tiktok]
              .filter(Boolean)
              .map((social) => (
                <Link
                  href={social.url}
                  key={social.platform}
                  target="_blank"
                  rel="noopener noreferrer"
                  background={{ base: "#222", md: "white" }}
                  rounded="lg"
                  alignItems="center"
                  justifyContent="center"
                  display="flex"
                  p={1}
                  px={2}
                >
                  <HStack color={{ base: "white", md: "black" }}>
                    <Icon
                      as={platformToIcons[social.platform]}
                      fontSize="16px"
                    />
                    {social.followers ? (
                      <Text fontSize="xs">{social.display_followers}</Text>
                    ) : null}
                  </HStack>
                </Link>
              ))}
          </HStack>
        </Stack>
        <Text color={{ base: "white", md: "black" }}>
          {formatDateToHours(performer.set_time)}
        </Text>
      </HStack>
    </HStack>
  );
};

function Lineup({
  performers,
  onToggleSetTimes,
  isSetTimesExpanded,
}: //display,
LineupProps) {
  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  return (
    <Stack
      //display={display}
      bg={{ base: "none", md: "white" }}
      w="full"
      roundedTop={{
        base: "none",
        md: "lg",
      }}
      spacing={2}
    >
      <HStack
        px={3}
        w="full"
        spacing={[0, 1]}
        gap={0}
        py={1}
        onClick={onToggleSetTimes}
        backgroundColor={{ base: "none", md: "gray.100" }}
        rounded={["none", "xl"]}
        justifyContent="space-between"
        alignItems="center"
      >
        {isSetTimesExpanded ? (
          <VStack w="full" spacing={0} gap={0}>
            <Box
              display={{ base: "none", md: "flex" }}
              alignItems="flex-start"
              w="full"
              pt={2}
            >
              <Text fontSize="sm" color="gray.500" fontWeight="bold" w="full">
                Line-up details
              </Text>
              <IconButton
                display={{ base: "none", md: "flex" }}
                rounded="full"
                backgroundColor="white"
                height={"24px"}
                width={"24px"}
                alignItems="center"
                justifyContent="center"
                border="1px solid"
                borderColor="gray.200"
                minW={"initial"}
                icon={<FaChevronUp fontSize="12px" />}
                aria-label="Toggle Set times"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleSetTimes();
                }}
              />
            </Box>
            {performers.map((performer, index) => (
              <PerformerRow
                key={`${performer.id}-${index}`}
                performer={performer}
                isLast={index === performers.length - 1}
              />
            ))}
            <Box
              pt={0}
              w="full"
              alignItems="flex-start"
              display={{ base: "block", md: "none" }}
            >
              <Button
                rounded="lg"
                size="sm"
                backgroundColor="#222222"
                fontWeight={"normal"}
                color="white"
                _hover={{
                  backgroundColor: "#333333",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleSetTimes();
                }}
              >
                Hide Set times
              </Button>
            </Box>
          </VStack>
        ) : (
          <HStack
            flexGrow={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <HStack flexGrow={1} width={"full"}>
              <HStack
                position="relative"
                width={[
                  `${40 + 32 * Math.min(performers.length, 4)}px`,
                  `${30 + 16 * Math.min(performers.length, 4)}px`,
                ]}
                height={["80px", "48px"]}
                overflow="hidden"
              >
                {performers
                  .slice(0, isMobile ? 4 : 8)
                  .map((performer, index) => (
                    <Box
                      key={performer.id}
                      w={["54px", "32px"]}
                      h={["54px", "32px"]}
                      rounded="full"
                      position={"absolute"}
                      left={[`${32 * index}px`, `${16 * index}px`]}
                      boxShadow="md"
                      overflow="hidden"
                      zIndex={100 - (index + 1)}
                      alignItems="center"
                      justifyContent="center"
                      display="flex"
                    >
                      <Image src={performer.image_url} alt={performer.name} />
                    </Box>
                  ))}
              </HStack>
              <VStack alignItems="flex-start" maxW="50%">
                <Text
                  color={{ base: "white", md: "black" }}
                  fontSize={"sm"}
                  fontWeight={"medium"}
                >
                  {performers
                    .slice(0, 4)
                    .map((p) => p.name)
                    .join(", ")}

                  {performers.length > 4 ? (
                    <Text
                      color={{ base: "white", md: "black" }}
                      fontSize={["sm", "xs"]}
                      fontWeight={["normal", "bold"]}
                    >
                      + {performers.length - 4} more
                    </Text>
                  ) : null}
                </Text>
              </VStack>
            </HStack>
            <IconButton
              display={{ base: "none", md: "flex" }}
              rounded="full"
              backgroundColor="white"
              height={"24px"}
              width={"24px"}
              alignItems="center"
              justifyContent="center"
              border="1px solid"
              borderColor="gray.200"
              minW={"initial"}
              icon={<FaChevronDown fontSize="12px" />}
              aria-label="Toggle Set times"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggleSetTimes();
              }}
            />
          </HStack>
        )}
      </HStack>
    </Stack>
  );
}

export default Lineup;
